import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { VALIDATE_AND_SYNC_SHOPIFY } from '../../apollo/mutations/validateAndSyncShopify';
import FormInput from '../../components/atoms/Input/Form-Input';
import Button from '../../components/atoms/Buttons';
import { createToast } from '../../utils/Toast';
import { useUserContext } from '../../context/UserContext';
import { encryptToken } from '../../utils/encryption';
import { useState } from 'react';
import MessageModal from '../../components/atoms/Modal/MessageModal/MessageModal';

const Toast = createToast();

function ShopifyForm() {
  const navigate = useNavigate();
  const { userData, refetchUser } = useUserContext();
  const [showSuccess, setShowSuccess] = useState(false);

  const methods = useForm({
    defaultValues: {
      shop: '',
      shopifyAccessToken: '',
    },
    mode: 'onBlur',
  });
  const { handleSubmit, formState: { isSubmitting } } = methods;

  const [validateAndSyncShopify, { loading: mutationLoading }] = useMutation(VALIDATE_AND_SYNC_SHOPIFY);

  const onSubmit = async ({ shop, shopifyAccessToken }) => {
    try {
      const brandName = userData?.brand || '';

      if (!brandName) {
        Toast.fire({
          icon: 'error',
          title: 'Brand information not found. Please try again.',
        });
        return;
      }

      const encryptedToken = await encryptToken(shopifyAccessToken.trim());

      const { data } = await validateAndSyncShopify({
        variables: {
          input: {
            brandName,
            shopifyAccessToken: encryptedToken,
            shop: shop?.trim(),
          },
        },
      });

      const result = data?.validateAndSyncShopify;

      if (result?.isShopifyConnected) {
        try {
          await refetchUser?.();
        } catch (e) { }
        setShowSuccess(true);
      } else {
        Toast.fire({
          icon: 'error',
          title: result?.message || 'Failed to connect Shopify store. Please verify your credentials.',
        });
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error?.message || 'Unable to connect Shopify store. Please try again.',
      });
    }
  };

  const isLoading = isSubmitting || mutationLoading;

  return (
    <>
      <FormProvider {...methods}>
        <form
          className="flex flex-col items-center justify-center gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="w-full max-w-sm">
            <FormInput
              name="shop"
              label="Shopify Store URL"
              placeholder="e.g. your-store.myshopify.com"
              validations={{
                required: 'Shopify Store URL is required',
                validate: (v) => {
                  let value = String(v || '')?.trim();

                  if (!value) return 'Shopify Store URL is required';

                  if (/\s/.test(value)) {
                    return 'URL cannot contain spaces';
                  }

                  value = value.replace(/^https?:\/\//, '');

                  if (value.includes('/')) {
                    return 'Only the store URL is allowed (e.g. your-store.myshopify.com).';
                  }

                  if (!/^[a-zA-Z0-9.-]+$/.test(value)) {
                    return 'Invalid characters in domain';
                  }

                  if (/\.\.+|--+/.test(value)) {
                    return 'Invalid domain format';
                  }

                  if (/^[.-]|[.-]$/.test(value)) {
                    return 'Invalid domain format';
                  }

                  if (!value.endsWith('.myshopify.com')) {
                    return 'Please enter a valid Shopify domain (e.g. your-store.myshopify.com)';
                  }

                  const storeName = value.replace('.myshopify.com', '');

                  if (!storeName || storeName.length === 0) {
                    return 'Please enter your store name';
                  }

                  if (/^[.-]|[.-]$/.test(storeName)) {
                    return 'Invalid store name format';
                  }

                  if (/--+|\.\.+/.test(storeName)) {
                    return 'Invalid store name format';
                  }

                  if (!/^[a-zA-Z0-9.-]+$/.test(storeName)) {
                    return 'Invalid store name format';
                  }

                  return true;
                },
              }}
            />
          </div>

          <div className="w-full max-w-sm">
            <FormInput
              name="shopifyAccessToken"
              label="Shopify Access Token"
              placeholder="Enter your Shopify access token"
              type="password"
              validations={{
                required: 'Shopify Access Token is required',
                validate: (v) => {
                  const value = String(v || '')?.trim();
                  if (!value) return 'Shopify Access Token is required';
                  if (value.length < 10) return 'Access token seems too short';
                  return true;
                },
              }}
            />
          </div>

          <Button
            className="create-saddle-btn !w-[220px] cursor-pointer"
            type="submit"
            loading={isLoading}
          >
            Connect Shopify
          </Button>
        </form>
      </FormProvider>

      <MessageModal
        title={'Shopify connection successful'}
        description={'Your Shopify store has been connected successfully. You can now manage your products and orders from the dashboard.'}
        showModal={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          navigate('/stripe-connect', { replace: true });
        }}
      />
    </>
  );
}

export default ShopifyForm;
